import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSupabaseQuery } from '../../hooks/useSupabase';
import {
  Users,
  Building,
  BarChart3,
  ArrowRight,
} from 'lucide-react';

export function DeptAdminDashboard() {
  const { profile } = useAuth();

  // Get department info
  const { data: department } = useSupabaseQuery('departments', {
    filter: (q: any) => q.eq('id', profile?.departmentId),
    single: true,
    enabled: !!profile?.departmentId,
  });

  // Get company info
  const { data: company } = useSupabaseQuery('companies', {
    filter: (q: any) => q.eq('id', department?.company_id),
    single: true,
    enabled: !!department?.company_id,
  });

  // Get users in department
  const { data: usersData } = useSupabaseQuery('profiles', {
    filter: (q: any) => q.eq('department_id', profile?.departmentId),
    enabled: !!profile?.departmentId,
  });
  const users = usersData || [];

  const activeUsers = users.filter((u: any) => u.is_active !== false);

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Velkommen til avdelingsadministrasjon
        </h1>
        <p className="text-green-100">
          Her kan du administrere brukere i din avdeling.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <Building className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Avdeling</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {department?.name || 'Laster...'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Aktive brukere</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {activeUsers.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Plasser</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {activeUsers.length} / {department?.allocated_seats || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Company Info */}
      {company && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Bedriftsinformasjon</h2>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
              <Building className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{company.name}</p>
              {company.email && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{company.email}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Action */}
      <Link
        to="/dept-admin/users"
        className="block bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6 hover:shadow-md transition-shadow group"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Administrer brukere
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Se, opprett og administrer brukere i din avdeling.
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors" />
        </div>
      </Link>
    </div>
  );
}
